// POListXL - Sprint 44 Fase 3 Vista 9.
// Lista PO con filtros (q, estado XLChip pills) + pager + msg auto-dismiss.
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ensureKeyframes, injectCSS } from '../../design/motion';
import apiClient from '../../api/apiClient';
import XLTable from '../../components/xl/XLTable';
import XLInput from '../../components/xl/XLInput';
import XLBadge from '../../components/xl/XLBadge';
import XLChip from '../../components/xl/XLChip';

const CSS_ID = 'xl-po-list-css';
const CSS = `
.xl-page { padding: 24px; font-family: var(--xl-font-sans); color: var(--xl-color-text) }
.xl-page__head { display: flex; align-items: flex-start; justify-content: space-between;
  gap: 16px; margin-bottom: 16px; flex-wrap: wrap; }
.xl-page__title { font-size: 22px; font-weight: 600; margin: 0 }
.xl-page__sub { font-size: 13px; color: var(--xl-color-text-secondary); margin: 2px 0 0 }
.xl-btn {
  font: 500 13px/1 var(--xl-font-sans); padding: 10px 16px;
  border-radius: var(--xl-radius-md); border: 1px solid var(--xl-color-border);
  background: transparent; color: var(--xl-color-text-secondary); cursor: pointer;
}
.xl-btn:hover { background: var(--xl-color-surface); color: var(--xl-color-text) }
.xl-btn--primary { background: var(--xl-color-primary); color: var(--xl-color-text-inverse); border-color: transparent }
.xl-btn--primary:hover { background: var(--xl-color-primary-hover) }
.xl-btn:disabled { opacity: 0.45; cursor: not-allowed }
.xl-btn:focus-visible { outline: 2px solid var(--xl-color-focus); outline-offset: 2px }
.xl-filters {
  background: var(--xl-color-surface); border: 1px solid var(--xl-color-border);
  border-radius: var(--xl-radius-lg); padding: 16px; margin-bottom: 16px;
  box-shadow: var(--xl-shadow-sm);
  display: flex; flex-direction: column; gap: 12px;
}
.xl-pills { display: flex; flex-wrap: wrap; gap: 6px }
.xl-banner {
  padding: 10px 14px; margin-bottom: 12px; border-radius: var(--xl-radius-md); font-size: 13px;
  background: rgba(220,38,38,0.10); color: var(--xl-color-danger);
  border: 1px solid rgba(220,38,38,0.28);
}
.xl-pager {
  display: flex; align-items: center; justify-content: space-between;
  gap: 12px; padding: 12px 4px; margin-top: 12px;
  color: var(--xl-color-text-secondary); font-size: 13px;
}
.xl-pager__group { display: flex; gap: 8px; align-items: center }
.xl-mono { font-family: var(--xl-font-mono); font-size: 12px }
`;

const PER_PAGE = 20;
const ESTADO_VARIANT = {
  draft: 'neutral', confirmed: 'info', recibido_parcial: 'warning',
  recibido_total: 'success', cancelled: 'danger',
};
const ESTADO_LABEL = {
  draft: 'Borrador', confirmed: 'Aprobada',
  recibido_parcial: 'Recibido parcial',
  recibido_total: 'Recibido total',
  cancelled: 'Cancelada',
};
const ESTADO_FILTROS = [
  { value: '',                 label: 'Todos' },
  { value: 'draft',            label: 'Borrador' },
  { value: 'confirmed',        label: 'Aprobada' },
  { value: 'recibido_parcial', label: 'Recibido parcial' },
  { value: 'recibido_total',   label: 'Recibido total' },
  { value: 'cancelled',        label: 'Cancelada' },
];

function fmt(v) {
  if (v === null || v === undefined) return '\u2014';
  return Number(v).toLocaleString('es-CO', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function useDebouncedSetter(setter, delay) {
  const [local, setLocal] = useState('');
  useEffect(() => {
    const t = setTimeout(() => setter(local), delay);
    return () => clearTimeout(t);
  }, [local, delay, setter]);
  return [local, setLocal];
}

export default function POListXL() {
  useEffect(() => { ensureKeyframes(); injectCSS(CSS_ID, CSS); }, []);
  const navigate = useNavigate();

  const [items, setItems] = useState([]);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);
  const [q, setQ] = useState('');
  const [estado, setEstado] = useState('');
  const [searchLocal, setSearchLocal] = useDebouncedSetter(setQ, 300);

  const load = useCallback(async () => {
    setLoading(true); setErr(null);
    try {
      const params = new URLSearchParams({ page: String(page), per_page: String(PER_PAGE) });
      if (q) params.set('q', q);
      if (estado) params.set('estado', estado);
      const r = await apiClient.get('/po/?' + params.toString());
      const d = (r && r.data) || {};
      setItems(d.items || []);
      setPages(Math.max(1, d.pages || 1));
      setTotal(d.total || 0);
    } catch (e) {
      setErr((e && e.message) || 'Error al cargar');
      setItems([]); setTotal(0); setPages(1);
    } finally { setLoading(false); }
  }, [page, q, estado]);

  useEffect(() => { load(); }, [load]);

  const setEstadoReset = (v) => { setPage(1); setEstado(v); };

  const columns = useMemo(() => ([
    { key: 'referencia', label: 'Referencia',
      render: (p) => <span className="xl-mono">{p.referencia}</span> },
    { key: 'proveedor', label: 'Proveedor',
      render: (p) => p.proveedor_nombre || '\u2014' },
    { key: 'fecha_po', label: 'Fecha',
      render: (p) => p.fecha_po || '\u2014' },
    { key: 'moneda', label: 'Moneda',
      render: (p) => <span className="xl-mono">{p.moneda || '\u2014'}</span> },
    { key: 'total_po_moneda', label: 'Total', align: 'right',
      render: (p) => <span className="xl-mono">{fmt(p.total_po_moneda)}</span> },
    { key: 'estado', label: 'Estado',
      render: (p) => (
        <XLBadge variant={ESTADO_VARIANT[p.estado] || 'neutral'} dot>
          {ESTADO_LABEL[p.estado] || p.estado || '\u2014'}
        </XLBadge>
      ) },
  ]), []);

  const canPrev = page > 1;
  const canNext = page < pages;

  return (
    <div className="xl-page">
      <header className="xl-page__head">
        <div>
          <h2 className="xl-page__title">Purchase Orders</h2>
          <p className="xl-page__sub">Total: {total}</p>
        </div>
        <button type="button" className="xl-btn xl-btn--primary"
          onClick={() => navigate('/po/create')}>+ Nueva PO</button>
      </header>

      <section className="xl-filters" aria-label="Filtros PO">
        <div className="xl-pills" role="tablist">
          {ESTADO_FILTROS.map((f) => (
            <XLChip key={f.value || 'all'}
              onClick={() => setEstadoReset(f.value)}
              selected={estado === f.value}>{f.label}</XLChip>
          ))}
        </div>
        <XLInput type="search" label="Buscar"
          placeholder="Referencia..." value={searchLocal}
          onChange={(e) => setSearchLocal(e.target.value)} />
      </section>

      {err && <div className="xl-banner" role="alert">{err}</div>}

      <XLTable ariaLabel="Lista PO"
        columns={columns} data={items} loading={loading}
        onRowClick={(p) => navigate('/po/' + p.id + '/edit')}
        rowKey="id" emptyMessage="Sin POs para estos filtros." />

      <div className="xl-pager">
        <span>{total ? ('Pagina ' + page + ' de ' + pages + ' \u00b7 ' + total + ' resultados') : 'Sin resultados'}</span>
        <span className="xl-pager__group">
          <button type="button" className="xl-btn"
            disabled={!canPrev || loading} onClick={() => setPage(page - 1)}>Anterior</button>
          <span className="xl-mono">{page} / {pages}</span>
          <button type="button" className="xl-btn"
            disabled={!canNext || loading} onClick={() => setPage(page + 1)}>Siguiente</button>
        </span>
      </div>
    </div>
  );
}
